import { useGet } from 'lib/api';
import { apiSuppliers } from 'api/suppliers';
import { produce, type WritableDraft } from 'immer';
import { t } from '@lingui/macro';
import type { MergeRequest, MergeRequestData, LegacyUser } from '../types';

const showDropdowns = (user: LegacyUser) => {
  const scenarios = ['Scenario 1', 'Scenario 4', 'Scenario 5', 'Scenario 6'];
  if (!user.alreadyInPortal) {
    return true;
  }
  if (user.alreadyInPortal && scenarios.includes(user.userMergeScenario)) {
    return true;
  }
  return false;
};

const showSkip = (userType: string, scenario: string) => {
  const hideScenarios = ['Scenario 7', 'Scenario 8'];
  if (userType === 'client_user') {
    if (hideScenarios.includes(scenario)) {
      return false;
    }
    return true;
  }
  return false;
};

const note = (userType: string, alreadyInPortal: boolean, scenario: string) => {
  if (alreadyInPortal && scenario) {
    switch (scenario) {
      case 'Scenario 1':
        return t`User already exists in portal. Role and permissions will be updated.`;
      case 'Scenario 2':
        return t`Already part of your account with supplier admin.`;
      case 'Scenario 3':
        return t`Already part of your account with supplier admin.`;
      case 'Scenario 4':
        return t`User already exists in portal. Role and permissions will be updated.`;
      case 'Scenario 5':
        return '';
      case 'Scenario 6':
        return '';
      case 'Scenario 7':
        return t`Already part of your account with supplier admin`;
      case 'Scenario 8':
        return t`Already part of your account with supplier admin`;
    }
  }
  switch (userType) {
    case 'supplier_admin':
      return alreadyInPortal
        ? t`Already part of your account with supplier admin.`
        : t`New user will be created in the portal.`;
    case 'supplier_user':
      return alreadyInPortal
        ? t`User already exists in portal. Role and permissions will be updated.`
        : t`New user will be created in the portal.`;
    case 'client_user':
      return alreadyInPortal
        ? t`User already exists in portal.`
        : t`User has client access and admin can choose to skip this user.`;
    default:
      return '';
  }
};

const adapter = (response: MergeRequest): MergeRequestData => {
  return produce(response, (draft: WritableDraft<MergeRequestData>) => {
    draft.data.legacyUsers = draft.data.legacyUsers.map(user => ({
      ...user,
      showDropdowns: showDropdowns(user),
      displayNote: note(user.type, user.alreadyInPortal, user.userMergeScenario),
      showSkip: showSkip(user.type, user.userMergeScenario),
    }));
    draft.data.notFound =
      !draft?.data?.legacyUsers || ['Approved', 'Rejected'].indexOf(draft?.data?.status || '') > -1;
  }) as MergeRequestData;
};

export const useGetMergeRequest = (mergeRequestId: string | null, supplierId: string) => {
  const { data, isLoading, error } = useGet<MergeRequest>(
    mergeRequestId ? apiSuppliers.get.mergeRequest : null,
    ['merge-request', mergeRequestId || ''],
    [supplierId, mergeRequestId || ''],
    adapter
  );
  return { data: data as MergeRequestData, isLoading, error };
};
